import { BaseField } from '@epages/react-components'
import { DndContext } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import type { List, Map } from 'immutable'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { useContext } from 'react'

import type { DraggableRefProps } from '../../utils/hooks/useSortableHelpers'
import { ExposeIsDragging, useSortableA11y, useSortableSensors } from '../../utils/hooks/useSortableHelpers'
import { ListItem } from './ListItem'
import { useTranslate } from '../../utils/translate'
import FormValue from '../formComponents/FormValue'

export default function FooterPagesPopover({ draggableRef }: DraggableRefProps) {
  const t = useTranslate('interface', 'components.bottomBar.footerSettings.sections.popovers.pageSelection')

  const footerPages: Core.Page[] = (useContext(BaseField.contextType) as any)?.instance?.getValue('footerPages')?.toJS()

  const a11y = useSortableA11y(t, (draggableId) => footerPages.find(({ id }) => id === draggableId)?.title || '')
  const sensors = useSortableSensors()

  return (
    <FormValue name="footerPages">
      {(footerPages: List<Map<string, keyof Core.Page>>, setFooterPages) => {
        function handleDragEnd({ active, over }: DragEndEvent) {
          if (!over || over.id === active.id) return
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const oldIndex = footerPages.findIndex((page) => page!.get('id') === active.id)
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const newIndex = footerPages.findIndex((page) => page!.get('id') === over.id)
          setFooterPages(footerPages.remove(oldIndex).splice(newIndex, 0, footerPages.get(oldIndex)))
        }

        return (
          <fieldset>
            <legend>{t('title')}</legend>
            <scroll-shadow>
              <div className="ep-footer-sections-settings-popover-column">
                <DndContext
                  accessibility={a11y}
                  sensors={sensors}
                  modifiers={[restrictToFirstScrollableAncestor, restrictToVerticalAxis]}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    items={footerPages.map((page) => page!.get('id')).toArray()}
                    strategy={verticalListSortingStrategy}
                  >
                    <ExposeIsDragging draggableRef={draggableRef} />
                    {footerPages
                      .map((page: ImmutableMap, index: number) => (
                        <ListItem key={page.get('id')} id={page.get('id')} index={index} name="footerPages" />
                      ))
                      .toArray()}
                  </SortableContext>
                </DndContext>
              </div>
            </scroll-shadow>
          </fieldset>
        )
      }}
    </FormValue>
  )
}

FooterPagesPopover.defaultValue = {}
